import { ParsedQuestion } from '../components/survey/types';
import { isPlaceholderQuestion } from './questionPlaceholder';

/**
 * Pure merge rule shared by the realtime-parsing hook and the questions
 * preview. It lives OUTSIDE the hook module on purpose: the preview needs the
 * same rule, and tests routinely `vi.mock` the hook — importing the rule from
 * there would make a mocked hook silently take the merge with it.
 *
 * There used to be two copies of this rule (one here, one inlined in
 * ParsedQuestionsPreview). Only one of them got fixed, so the preview kept
 * re-applying a stale type on top of a corrected re-parse. One copy now.
 */
export function normalizeText(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Merge user edits from a cached question onto a fresh API result.
 *
 * - Fields from **parsing** (choices, matrix data) always use fresh —
 *   they are re-extracted from the latest textarea text each time.
 *   `null` from the API means "no choices" (user removed them), NOT
 *   "unknown"; we must NOT fall back to stale cached choices.
 * - The **type** (and its dependent config: scale/max/ranking/limit) prefers
 *   cached when the cached value is an EXPLICIT user choice (`userSetType`) and
 *   this is still recognisably THE SAME QUESTION, or when the text is unchanged.
 *   The cache is seeded on every parse, so a cached type is often parser-
 *   assigned, not user-set — preferring a parser-assigned type across a text
 *   change kept a changed question stuck on its old type (e.g. "hi" →
 *   info_screen stayed info_screen after being edited into a real question).
 *   A changed text with no user pin means the parser re-classified: use fresh.
 * - Images are attached independently of the text, so they still prefer cached.
 */
/** A Pass-1 placeholder's text must NOT be treated as a genuine edit, or a
 *  round-trip through a skeleton would drop the user's type. */
const isSkeleton = isPlaceholderQuestion;

/**
 * True when `a` and `b` are the same question lightly reworded (a boundary
 * shift, a "Q1." prefix, a typo fix) rather than a wholesale replacement.
 * Question ids are POSITIONAL (Q1, Q2...), so we can't trust the id alone to
 * mean "same question"; we use text similarity as the tie-breaker. Measures the
 * shared leading + trailing run and requires it to cover most of the longer
 * text — the same prefix/suffix heuristic the hook uses to decide cache resets.
 */
function isMinorTextEdit(a: string, b: string): boolean {
  const x = normalizeText(a);
  const y = normalizeText(b);
  if (x === y) return true;
  const minLen = Math.min(x.length, y.length);
  let prefix = 0;
  while (prefix < minLen && x[prefix] === y[prefix]) prefix++;
  let suffix = 0;
  const maxSuffix = minLen - prefix;
  while (suffix < maxSuffix && x[x.length - 1 - suffix] === y[y.length - 1 - suffix]) suffix++;
  const shared = prefix + suffix;
  const longer = Math.max(x.length, y.length);
  return longer > 0 && shared >= longer * 0.5;
}

export function mergeWithCached(fresh: ParsedQuestion, cached: ParsedQuestion): ParsedQuestion {
  const sameText = normalizeText(cached.text) === normalizeText(fresh.text);
  // Keep the cached type while this is still THE SAME QUESTION:
  //  - same text — the parser had nothing new to say; a user's pick survives.
  //  - an EXPLICIT user pick (`userSetType`) that survives a light rewording
  //    (skeleton round-trip, or a minor text edit) — the user's intent wins over
  //    a re-classification. A wholesale text replacement at this positional id is
  //    a DIFFERENT question, so the pin is dropped and the parser re-classifies.
  // A changed text with no user pin means the parser re-classified: fresh wins.
  const keepType =
    sameText ||
    (cached.userSetType === true &&
      (isSkeleton(fresh) || isMinorTextEdit(cached.text, fresh.text)));
  return {
    ...fresh,
    type: keepType ? cached.type : fresh.type,
    userSetType: keepType ? cached.userSetType : undefined,
    scale_min: keepType ? (cached.scale_min ?? fresh.scale_min) : fresh.scale_min,
    scale_max: keepType ? (cached.scale_max ?? fresh.scale_max) : fresh.scale_max,
    max_selections: keepType
      ? (cached.max_selections ?? fresh.max_selections)
      : fresh.max_selections,
    ranking_count: keepType ? (cached.ranking_count ?? fresh.ranking_count) : fresh.ranking_count,
    text_limit: keepType ? (cached.text_limit ?? fresh.text_limit) : fresh.text_limit,
    image_paths: cached.image_paths?.length ? cached.image_paths : fresh.image_paths,
    image_descriptions: cached.image_descriptions?.length
      ? cached.image_descriptions
      : fresh.image_descriptions,
    image_attachment_level: cached.image_attachment_level ?? fresh.image_attachment_level,
    option_images:
      cached.option_images && Object.keys(cached.option_images).length > 0
        ? cached.option_images
        : fresh.option_images,
    // Parsed-from-text fields: always use fresh (no fallback to stale cache)
    choices: fresh.choices,
    maxdiff_items: fresh.maxdiff_items,
    conjoint_attributes: fresh.conjoint_attributes,
    conjoint_description: fresh.conjoint_description,
    matrix_rows: fresh.matrix_rows,
    matrix_columns: fresh.matrix_columns,
  };
}
